import serializeForm from '../utils/consultation/serialize-form';
import { postToLeadWithCaptcha } from '../utils/post-to-lead-with-captcha';
import { initCaptchaForForm } from '../utils/captcha-v2';
import { validate } from '../utils/validation';
import { paperPlaneAnimation, paperPlaneAnimationDone } from './button-paper-plane';
import attachTrackingPayload from '../utils/tracking-payload';
import { getLeadDimensions } from '../utils/ga4-tracking/custom-dimensions';
import { pushToDataLayer } from '../utils/ga4-tracking/push-to-data-layer';
import { pageSettings } from '../utils/ga4-tracking/page-settings';
import { debounce } from '../utils/utils';
import { slideDown, slideUp } from '../startup/slide';
import { NotificationContainerHandler } from '../forms/form-library';

const addClickLeadmagnet = block => {
  if (block.dataset.leadmagnetInitialized === 'true') {
    return;
  }

  block.dataset.leadmagnetInitialized = 'true';

  const box = block.querySelector('.box');
  const leadText = block.querySelector('.lead-text');
  const confirmMailText = block.querySelector('.confirm-mail');
  const formLead = block.querySelector('form');

  if (!box || !formLead) {
    return;
  }

  const emailButtons = block.querySelector('.email-btns');
  const button = formLead.querySelector('.submitter');

  if (!button) {
    return;
  }

  const captchaMountElement = formLead.querySelector('.captaincaps');
  let captchaController;
  const documentType = formLead.dataset.doctype || (box.classList.contains('newsletter') ? 'newsletter' : 'leadmagnet');
  const mediaMatcherMinSm = window.matchMedia('(min-width: 768px)');
  const notificationHandler = new NotificationContainerHandler(box);

  const leadmagnetLangCheck = en || ['whitepaper-visa', 'whitepaper-health-insurance', 'working-visa-en', 'pkv-abc-en', 'after-study-guide-en'].some(cls => formLead.dataset.doctype === cls);

  const prepareText = leadmagnetLangCheck
    ? `<span>Preparing <span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`
    : `<span>Vorbereitung läuft <span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`;

  const emailInput = formLead.querySelector('input[name="email"]');

  const setButtonDisabled = isDisabled => {
    button.disabled = isDisabled;
    button.classList.toggle('disabled', isDisabled);
    button.classList.toggle('active', !isDisabled);
  };

  let emailInvalid = false;

  const alerts = {
    default: formLead.querySelector('.alert.default'),
    email: formLead.querySelector('.alert.email')
  };

  const hasVisibleRequiredFields = () => {
    const requiredFields = Array.from(formLead.querySelectorAll('[required]'));

    return requiredFields.every(field => {
      if (!(field instanceof HTMLElement) || field.disabled) {
        return true;
      }

      const hiddenByClass = field.classList.contains('is-hidden') || field.closest('.is-hidden');
      if (hiddenByClass) {
        return true;
      }

      if ('checkValidity' in field && typeof field.checkValidity === 'function') {
        return field.checkValidity();
      }

      return true;
    });
  };

  let requireCaptchaOnSubmit = false;

  const { controller, requireCaptchaOnSubmit: capturedRequireCaptchaOnSubmit } = initCaptchaForForm({
    form: formLead,
    mountElement: captchaMountElement,
    emailInput,
    locale: en ? 'en' : 'de',
    onComplete: () => {
      refreshSubmitState();
    },
    onExpire: () => {
      refreshSubmitState();
    },
    onError: () => {
      refreshSubmitState();
    },
    onShowWidget: () => {
      captchaMountElement?.classList.remove('is-hidden');
      button.classList.add('is-hidden');
      refreshSubmitState();
    },
    onHideWidget: () => {
      captchaMountElement?.classList.add('is-hidden');
      button.classList.remove('is-hidden');
      refreshSubmitState();
    }
  });
  captchaController = controller;
  requireCaptchaOnSubmit = capturedRequireCaptchaOnSubmit;

  formLead.addEventListener(
    'input',
    () => {
      if (captchaController && !captchaController.hasUsableToken()) {
        captchaController.startIfNeeded();
      }
    },
    { once: true }
  );

  function refreshSubmitState() {
    const hasCaptchaToken = !requireCaptchaOnSubmit || Boolean(captchaController?.hasUsableToken());
    const fieldsValid = hasVisibleRequiredFields();
    const canSubmit = Boolean(fieldsValid && hasCaptchaToken && !emailInvalid && !formLead.submitted && !button.classList.contains('done'));

    setButtonDisabled(!canSubmit);
  }

  emailInput?.addEventListener('input', () => {
    if (emailInvalid) {
      button.style.removeProperty('--plane-color');
      emailInvalid = false;
    }
    Object.values(alerts).forEach(alert => {
      slideUp(alert);
    });
    refreshSubmitState();
  });

  formLead.addEventListener('change', refreshSubmitState);

  async function paperPlane() {
    paperPlaneAnimation(button);
    await new Promise(resolve => setTimeout(resolve, 3000));
    paperPlaneAnimationDone(button);
  }

  button.addEventListener('click', async e => {
    e.preventDefault();
    const buttonInitText = button.querySelector('span.default')?.innerText?.trim() || (documentType !== 'newsletter' ? 'Kostenfreies PDF anfordern' : 'Jetzt eintragen');

    if (button.disabled) {
      return;
    }

    const validationResult = validate.validateAll(formLead);
    if (validationResult.length || button.classList.contains('done') || formLead.submitted) {
      if (validationResult.length) {
        notificationHandler.setValidateError(validationResult, button);
      }
      return;
    }
    button.querySelector('span.default').innerHTML = prepareText;
    const contactDetails = serializeForm(formLead);
    if (documentType !== 'newsletter') {
      contactDetails.document_name = documentType;
    }
    if (document.referrer.includes('secprf.com')) {
      pushToDataLayer({
        event: 'fake_lead',
        element_section: documentType,
        referrer: document.referrer
      });
      return;
    }

    contactDetails.landing_page_language = language;
    contactDetails.insurance_group = pageSettings.insuranceGroup;
    const payload = { contact_details: contactDetails };
    formLead.submitted = true;
    await attachTrackingPayload(payload);
    let endpointType = '/newsletter';
    if (!block.querySelector('.box').classList.contains('newsletter')) {
      endpointType = ['working-visa-de', 'working-visa-en', 'financial-de', 'after-study-guide-en', 'after-study-guide-de'].includes(documentType) ? '/ebook' : '/leadmagnet';
    }
    postToLeadWithCaptcha(endpointType, payload, {
      captcha: captchaController,
      requireCaptcha: requireCaptchaOnSubmit
    })
      .then(async data => {
        if (data instanceof Error) {
          throw data;
        }
        const boxHeight = box.offsetHeight;
        box.style.height = `${boxHeight}px`;
        emailInput.disabled = true;
        await paperPlane();
        if (confirmMailText) {
          setTimeout(() => {
            leadText.classList.add('move-out');
            formLead.classList.add('move-out');
            setTimeout(() => {
              leadText.classList.add('hidden');
              formLead.classList.add('hidden');
              confirmMailText.classList.remove('hidden');
              emailButtons.classList.remove('hidden');
              let newBoxHeight;
              if (mediaMatcherMinSm.matches) {
                if (confirmMailText.offsetHeight + emailButtons.offsetHeight + parseInt(getComputedStyle(box).gap.replace('px', '')) > boxHeight) {
                  newBoxHeight = confirmMailText.offsetHeight + emailButtons.offsetHeight + parseInt(getComputedStyle(box).gap.replace('px', ''));
                } else {
                  newBoxHeight = boxHeight;
                }
              } else {
                newBoxHeight =
                  parseInt(getComputedStyle(box).gap.replace('px', '')) +
                  parseInt(getComputedStyle(confirmMailText).marginTop.replace('px', '')) +
                  parseInt(getComputedStyle(confirmMailText).marginBottom.replace('px', '')) +
                  parseInt(getComputedStyle(emailButtons).marginTop.replace('px', '')) +
                  confirmMailText.offsetHeight +
                  emailButtons.offsetHeight;
                if (
                  box.classList.contains('working-visa-de') ||
                  box.classList.contains('working-visa-en') ||
                  box.classList.contains('financial-de') ||
                  box.classList.contains('pkv-abc-de') ||
                  box.classList.contains('pkv-abc-en')
                ) {
                  newBoxHeight += block.querySelector('.leadicons').offsetHeight + parseInt(getComputedStyle(box).gap.replace('px', ''));
                }
              }
              box.style.height = `${newBoxHeight}px`;
              setTimeout(() => {
                confirmMailText.classList.remove('move-in');
                emailButtons.classList.remove('move-in');
                emailButtons.querySelector('a').focus();
              }, 500);
            }, 500);
          }, 2000);
        } else {
          notificationHandler.setAriaMessage(leadmagnetLangCheck ? 'Email successfully sent' : 'E-Mail erfolgreich gesendet', [], button, true);
        }

        //New GA4 Tracking
        pushToDataLayer({
          event: 'email_lead',
          element_section: documentType !== 'newsletter' ? 'leadmagnet-block' : 'newsletter-block',
          element_detail: contactDetails.lead_source,
          link_text: buttonInitText.split('\n')[0],
          doi_status: data.consent_confirmed ? 'validated' : 'pending',
          leadsUserData: {
            email_address: payload.contact_details.email
          },
          ...getLeadDimensions({}, { includeProduct: true })
        });
      })
      .catch(error => {
        if (error.message.includes('must be an email')) {
          slideDown(alerts.email);
          notificationHandler.setAriaMessage(alerts.email.textContent, [emailInput], button);
          button.style.setProperty('--plane-color', 'var(--sec-dark-60)');
          setButtonDisabled(true);
          formLead.submitted = false;
          button.querySelector('span.default').innerHTML = en ? 'Try again' : 'Erneut versuchen';
          emailInvalid = true;
          emailInput.focus();
        } else {
          notificationHandler.setAriaMessage(alerts.default.textContent, [], button, true);
          button.style.backgroundColor = 'var(--danger)';
          setButtonDisabled(true);
          button.innerText = en ? 'Error' : 'Fehler';
          slideDown(alerts.default);
        }
      });
  });

  refreshSubmitState();

  window.addEventListener(
    'resize',
    debounce(() => {
      box.style.removeProperty('height');
    }, 300)
  );
};

document.querySelectorAll('.leadmagnet-block, .mag-wide-newsletter, .mag-wide-leadmagnet-block').forEach(block => {
  try {
    addClickLeadmagnet(block);
  } catch (error) {
    console.error('Lead magnet or newsletter initialization failed', error, block);
  }
});
